/**
 * Enriches a raw activity with resolved creator/player attribution.
 * Extracts the shared logic duplicated in ActivityFeedContainer's renderItem
 * and WhatsNewContainer's allActivities mapping, which both resolve
 * effectiveCreatorId, playerId, and enhanceActivityTitle identically.
 * @module utils/enrichActivityItem
 */

import { enhanceActivityTitle } from "@/utils/handleActivityTitle";
import { isNoteActivity } from "@/utils/isNoteActivity";

/** Minimal activity shape required for enrichment */
interface RawActivity {
  readonly activityType: string;
  readonly metadata: Record<string, unknown> | null | undefined;
  /** Acting user. Null for system-generated activities (e.g. contract-expiring alerts). */
  readonly userId: string | null | undefined;
  readonly targetId: string | null | undefined;
  readonly title: string;
  readonly notificationTitle?: string | null;
  readonly body?: string | null;
  readonly notificationBody?: string | null;
}

/** Maps used to resolve display names */
interface ResolutionMaps {
  readonly userIdToNameMap: Record<string, string>;
  readonly playerNames: Record<string, string>;
  readonly noteIdToPlayerIdMap: Record<string, string>;
  readonly currentUserId: string | undefined;
}

/** Result of enriching an activity item */
export interface EnrichedActivity {
  /** Resolved creator. Undefined for system-generated activities with no acting user. */
  readonly effectiveCreatorId: string | undefined;
  readonly playerId: string | undefined;
  readonly enhancedTitle: string;
}

/**
 * Resolves creator attribution, player ID, and enhanced title for an activity.
 * Handles CONTENT_SHARED's sharerUserId, note-activity targetId disambiguation,
 * and noteIdToPlayerIdMap fallback (SE-3990).
 * @param raw - The raw activity data
 * @param maps - Resolution maps for user names, player names, and note-to-player IDs
 * @returns Enriched activity with resolved creator, player, and title
 */
export const enrichActivityItem = (
  raw: RawActivity,
  maps: ResolutionMaps
): EnrichedActivity => {
  const { activityType, metadata, userId, targetId } = raw;
  const { userIdToNameMap, playerNames, noteIdToPlayerIdMap, currentUserId } =
    maps;

  // SE-3990: For CONTENT_SHARED, activity.userId is the recipient —
  // use metadata.sharerUserId to identify the actual sharer
  // SE-4894: userId is null for system-generated activities — normalize to undefined
  const effectiveCreatorId =
    (activityType === "CONTENT_SHARED"
      ? (metadata?.sharerUserId as string | undefined) || userId
      : userId) ?? undefined;

  const creatorName = effectiveCreatorId
    ? userIdToNameMap[effectiveCreatorId]
    : undefined;

  // SE-3989: For note activities, targetId is the note ID — not a player ID
  // SE-3990: Fall back to noteIdToPlayerIdMap when metadata.playerId is missing
  const metadataPlayerId = metadata?.playerId as string | undefined;
  const noteActivity = isNoteActivity(activityType, metadata);
  const noteId = (metadata?.noteId as string | undefined) || targetId;
  const noteResolvedPlayerId = noteId ? noteIdToPlayerIdMap[noteId] : undefined;
  const playerId =
    (noteActivity
      ? metadataPlayerId || noteResolvedPlayerId
      : metadataPlayerId || targetId) || undefined;

  const resolvedPlayerName = playerId ? playerNames[playerId] : undefined;

  const enhancedTitle = enhanceActivityTitle(
    activityType,
    raw.title,
    raw.notificationTitle,
    metadata,
    raw.body ?? undefined,
    raw.notificationBody,
    effectiveCreatorId,
    currentUserId,
    creatorName,
    resolvedPlayerName
  );

  return { effectiveCreatorId, playerId, enhancedTitle };
};
